import { Box, Button, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import { Formik } from 'formik';
import { Trash } from 'iconsax-react';
import { DateTime } from 'luxon';
import type { FC, ReactElement } from 'react';

import {
  FormikUserReminder,
  type UserReminderFormikType,
  UserReminderSchemaFormik,
  formikUserReminderToWire,
  userReminderRecordChange,
} from '@core/formik/userReminder';
import type { Activity } from '@core/types/api/Activity/Activity';
import Enums from '@core/types/api/Enums';
import type { Project } from '@core/types/api/Project/Project';
import type { Quote } from '@core/types/api/Quote/Quote';
import type { ReminderUserSetting } from '@core/types/api/ReminderUserSetting/ReminderUserSetting';
import type { InputOption } from '@core/types/app';
import { hasValue } from '@core/utils/equality';
import { FormErrorNotification } from '@core/utils/formikHelpers';
import type { NotificationScopeObject } from '@core/utils/notifications';
import { ReminderBusinessDatesByRecordType } from '@core/utils/reminderRecordTypes';
import { REMINDER_MINUTES_STEP } from '@core/utils/reminders';
import { useSlabFormQuery } from '@web/hooks/useSlabFormQuery';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { ServerErrorMessage, useSlabQuery } from '@web/hooks/useSlabQuery';
import { useConfirmDialog } from '@web/providers/ConfirmProvider/useConfirmDialog';
import { useToast } from '@web/providers/ToastProvider/useToast';

import { DurationOffsetSelector } from '../DurationOffsetSelector/DurationOffsetSelector';
import {
  EntitySearchInput,
  type EntitySearchSiblingValues,
  type EntitySelection,
} from '../EntitySearchInput/EntitySearchInput';
import { Input } from '../Input/Input';
import { InputDropdown } from '../InputDropdown/InputDropdown';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { FOOTER_BUTTON_SX, SlabButton } from '../SlabButton/SlabButton';
import { DrawerCloseHandler } from '../SlabDrawer/DrawerCloseHandler';
import { SlabDrawer } from '../SlabDrawer/SlabDrawer';
import { SlabDrawerHeader } from '../SlabDrawer/SlabDrawerHeader';
import { SlabDrawerLayout } from '../SlabDrawer/SlabDrawerLayout';
import { useDrawerCloseGuard } from '../SlabDrawer/useDrawerCloseGuard';
import { ReminderTriggerHint } from './ReminderTriggerHint';
import { ReminderTriggerHintBanner } from './ReminderTriggerHintBanner';

const businessDateOptions = (recordType: Enums.ReminderUserSettingsRecordType): InputOption[] =>
  ReminderBusinessDatesByRecordType[recordType].map((businessDate) => ({
    value: businessDate,
    label: businessDate,
  }));

const getInitialSelectedRecord = (
  reminder: ReminderUserSetting | undefined,
): EntitySelection | null => {
  const project = reminder?.recordData?.project;
  if (hasValue(project)) {
    return { id: project.id, type: 'projects', name: project.name };
  }
  const quote = reminder?.recordData?.quote;
  if (hasValue(quote)) {
    return { id: quote.id, type: 'quotes', name: quote.name ?? quote.quoteNumber };
  }
  const activity = reminder?.recordData?.activity;
  if (hasValue(activity)) {
    return { id: activity.id, type: 'activities', name: activity.name };
  }
  return null;
};

const getPrefillSelection = (
  quote: Quote | undefined,
  project: Project | undefined,
  activity: Activity | undefined,
): EntitySelection | null => {
  if (hasValue(project)) {
    return { id: project.id, type: 'projects', name: project.name };
  }
  if (hasValue(quote)) {
    return { id: quote.id, type: 'quotes', name: quote.name ?? quote.quoteNumber };
  }
  if (hasValue(activity)) {
    return { id: activity.id, type: 'activities', name: activity.name };
  }
  return null;
};

const withPrefilledRecord = (
  values: UserReminderFormikType,
  selection: EntitySelection | null,
): UserReminderFormikType => {
  if (!hasValue(selection)) {
    return values;
  }

  return {
    ...values,
    recordId: selection.id,
    ...userReminderRecordChange(values.recordBusinessDate, selection.type),
  };
};

type Props = {
  isOpen: boolean;
  resourceID: string | null;
  prefillRecord?: NotificationScopeObject | null;
  onClose: () => void;
};

const UserReminderDrawerPage: FC<Omit<Props, 'isOpen'>> = ({
  resourceID,
  prefillRecord = null,
  onClose,
}) => {
  const confirm = useConfirmDialog();

  const isNew = resourceID === null;
  const prefillType = isNew && hasValue(prefillRecord) ? prefillRecord.type : null;

  const toast = useToast({ subjectName: 'Reminder' });
  const { guardClose, editTracker } = useDrawerCloseGuard();

  const onMutationSuccess = (): void => {
    toast.success();
    onClose();
  };

  const createNew = useSlabMutation('POST reminder user setting', {
    onSuccess: onMutationSuccess,
    onError: (error) => {
      toast.error({ message: ServerErrorMessage(error) });
    },
  });

  const updateExisting = useSlabMutation('PUT reminder user setting by ID', {
    onSuccess: onMutationSuccess,
    onError: (error) => {
      toast.error({ message: ServerErrorMessage(error) });
    },
  });

  const { mutateAsync: archiveReminder, isPending: isArchivePending } = useSlabMutation(
    'POST bulk update reminder user settings',
    {
      onSuccess: () => {
        toast.success({ message: 'Reminder deleted successfully' });
        onClose();
      },
      onError: (error) => {
        toast.error({ message: ServerErrorMessage(error) });
      },
    },
  );

  const {
    isLoading: isLoadingReminder,
    isError: isErrorReminder,
    data: maybeReminder,
  } = useSlabFormQuery(
    'GET reminder user setting by ID',
    {
      pathParams: {
        id: resourceID ?? '',
      },
    },
    { enabled: !isNew },
  );

  const { isLoading: isLoadingPrefillQuote, data: prefillQuote } = useSlabQuery(
    'GET quote by ID',
    { pathParams: { id: prefillRecord?.id ?? '' } },
    { enabled: prefillType === 'quote' },
  );

  const { isLoading: isLoadingPrefillProject, data: prefillProject } = useSlabQuery(
    'GET project by ID',
    { pathParams: { id: prefillRecord?.id ?? '' } },
    { enabled: prefillType === 'project' },
  );

  const { isLoading: isLoadingPrefillActivity, data: prefillActivity } = useSlabQuery(
    'GET activity by ID',
    { pathParams: { id: prefillRecord?.id ?? '' } },
    { enabled: prefillType === 'activity' },
  );

  const confirmArchive = (): void => {
    if (!maybeReminder) {
      return;
    }
    confirm.alert({
      title: 'Confirm Delete',
      description: `Are you sure you want to delete this reminder?`,
      actions: [
        { label: 'Cancel', variant: 'slab-primary' },
        {
          label: 'Delete',
          variant: 'slab-secondary',
          onClick: (): void => {
            archiveReminder({
              args: { body: [{ id: maybeReminder.id, isArchived: true }] },
            });
          },
        },
      ],
    });
  };

  if (
    isLoadingReminder ||
    isLoadingPrefillQuote ||
    isLoadingPrefillProject ||
    isLoadingPrefillActivity
  ) {
    return <LoadingSpinner />;
  }

  if (isErrorReminder) {
    return (
      <Box padding='5rem 3.5rem 5rem 3.5rem'>
        <Typography variant='h1' fontSize='2rem'>
          Error loading system reminder
        </Typography>
      </Box>
    );
  }

  const prefillSelection = getPrefillSelection(
    prefillType === 'quote' ? prefillQuote : undefined,
    prefillType === 'project' ? prefillProject : undefined,
    prefillType === 'activity' ? prefillActivity : undefined,
  );
  const initialSelectedRecord = getInitialSelectedRecord(maybeReminder) ?? prefillSelection;
  const initialValues = withPrefilledRecord(FormikUserReminder(maybeReminder), prefillSelection);
  const isArchived = maybeReminder?.isArchived === true;

  return (
    <Formik
      validationSchema={UserReminderSchemaFormik()}
      initialValues={initialValues}
      onSubmit={(values): void => {
        const wireReminder = formikUserReminderToWire(values);
        if (isNew) {
          createNew.mutate({
            args: {
              body: wireReminder,
            },
          });
        } else {
          updateExisting.mutate({
            args: {
              pathParams: {
                id: values.id,
              },
              body: wireReminder,
            },
          });
        }
      }}
    >
      {(formikBag): ReactElement => {
        const closeDrawer = (): void => {
          formikBag.resetForm({ values: initialValues });
          onClose();
        };
        const isGeneralBusinessDate =
          formikBag.values.recordBusinessDate === Enums.ReminderUserSettingsBusinessDate.General;
        const isMutating = createNew.isPending || updateExisting.isPending;

        const requestClose = (): void =>
          guardClose({
            isDirty: !isArchived && editTracker.isEdited(),
            canSave: formikBag.isValid,
            isSaving: isMutating,
            onSave: formikBag.submitForm,
            onDiscard: closeDrawer,
          });

        return (
          <SlabDrawerLayout>
            <DrawerCloseHandler onClose={requestClose} editTracker={editTracker} />
            <Box>
              <FormErrorNotification />
              <SlabDrawerHeader
                title={`${isNew ? 'Create' : 'Edit'} Reminder`}
                onClose={requestClose}
              />
              <Box paddingY='1.25rem' display='flex' flexDirection='column' gap='1rem'>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <EntitySearchInput
                      name='recordId'
                      label='Record*'
                      entityTypes={['projects', 'quotes', 'activities']}
                      initialSelection={initialSelectedRecord}
                      onSelect={({ type }): EntitySearchSiblingValues =>
                        userReminderRecordChange(formikBag.values.recordBusinessDate, type)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputDropdown
                      name='recordBusinessDate'
                      label='Business Date*'
                      options={businessDateOptions(formikBag.values.recordType)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Input
                      name='note'
                      label='Note'
                      type='textarea'
                      placeholder='Reminder notes...'
                    />
                  </Grid>
                </Grid>
              </Box>
              <Divider />
              <Box paddingY='1.25rem' display='flex' flexDirection='column' gap='1rem'>
                <Typography variant='h2'>Reminder</Typography>
                {isGeneralBusinessDate ? (
                  <>
                    <Typography variant='body1' color='text.secondary'>
                      Select the date and time you&apos;d like to receive the reminder
                    </Typography>
                    <Input
                      name='notifyAt'
                      label='Fire Date and Time*'
                      type='datetime'
                      minutesStep={REMINDER_MINUTES_STEP}
                    />
                    {formikBag.values.notifyAt ? (
                      <ReminderTriggerHintBanner
                        triggerDate={DateTime.fromISO(formikBag.values.notifyAt)}
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    <Typography variant='body1' color='text.secondary'>
                      Select how far in advance you&apos;d like to receive the reminder
                    </Typography>
                    <DurationOffsetSelector name='notifyBefore' />
                    <ReminderTriggerHint />
                  </>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              {!isArchived && !isNew && hasValue(maybeReminder) && (
                <Button
                  color='inherit'
                  sx={FOOTER_BUTTON_SX}
                  disabled={isArchivePending}
                  startIcon={<Trash size={20} />}
                  variant='text'
                  onClick={confirmArchive}
                >
                  {isArchivePending ? <CircularProgress size={20} color='inherit' /> : 'Delete'}
                </Button>
              )}
              <SlabButton
                sx={FOOTER_BUTTON_SX}
                variant='slab-secondary'
                onClick={requestClose}
                disabled={isMutating || isArchivePending}
              >
                Cancel
              </SlabButton>
              <SlabButton
                sx={FOOTER_BUTTON_SX}
                variant='slab-secondary'
                onClick={(): void => formikBag.resetForm({ values: initialValues })}
                disabled={!formikBag.dirty || isMutating || isArchivePending}
              >
                Reset
              </SlabButton>
              <SlabButton
                sx={FOOTER_BUTTON_SX}
                variant='slab-primary'
                onClick={formikBag.submitForm}
                loading={isMutating}
                disabled={isMutating || (!isNew && !formikBag.dirty)}
              >
                Save
              </SlabButton>
            </Box>
          </SlabDrawerLayout>
        );
      }}
    </Formik>
  );
};

export const UserReminderDrawer: FC<Props> = ({ isOpen, onClose, ...props }) => (
  <SlabDrawer
    paperProps={{
      sx: {
        width: '60rem',
        maxWidth: '100%',
      },
    }}
    isOpen={isOpen}
    close={onClose}
  >
    <UserReminderDrawerPage onClose={onClose} {...props} />
  </SlabDrawer>
);
